/* cesiumTiles.js — Babylon.js port
 *
 * Google Photorealistic 3D Tiles (Cesium Ion asset 2275207) using
 * NASA-AMMOS `3d-tiles-renderer` with its NATIVE Babylon.js renderer.
 *
 * CRITICAL: scene MUST have `scene.useRightHandedSystem = true`.
 *
 * The Babylon renderer can only safely register framework-agnostic
 * plugins from `3d-tiles-renderer/core/plugins`. Plugins under
 * `/three/plugins` (re-exported via `/plugins`) call
 * `tiles.forEachLoadedModel()` and other THREE-only helpers, which
 * crash on the Babylon TilesRenderer. We therefore implement the
 * Reorientation step manually with WGS-84 ECEF→ENU math.
 *
 * Usage (same export shape as the Three.js original):
 *   const tiles = createCesiumIonTiles({
 *     lat, lon, scene, camera, token, assetId,
 *   });
 *   scene.onBeforeRenderObservable.add(() => tiles.update());
 *   tiles.dispose();
 */
import * as BABYLON from "@babylonjs/core";
import { TilesRenderer } from "3d-tiles-renderer/babylonjs";
import { CesiumIonAuthPlugin } from "3d-tiles-renderer/core/plugins";


export function createCesiumIonTiles({
  lat, lon, height = 0,
  scene, camera,
  token, assetId,
  lowBandwidth = false,
}) {
  if (!token) throw new Error("Cesium Ion token missing");
  if (!assetId) throw new Error("Cesium Ion assetId missing");
  if (!scene || !camera) throw new Error("scene/camera required");

  // ── CRITICAL: right-handed coordinate system ──
  if (!scene.useRightHandedSystem) {
    console.warn("[CesiumTiles] Forcing scene.useRightHandedSystem = true");
    scene.useRightHandedSystem = true;
  }

  // ── Create the Babylon.js-native TilesRenderer ──
  const tiles = new TilesRenderer(undefined, scene);

  // Framework-agnostic Cesium Ion auth plugin (handles token exchange
  // and tileset URL resolution).
  tiles.registerPlugin(new CesiumIonAuthPlugin({
    apiToken: token,
    assetId: String(assetId),
    autoRefreshToken: true,
  }));

  // ── Manual ECEF → local scene reorientation ──
  // 3D Tiles content is rooted at Earth's centre (ECEF). We want
  // (lat, lon, height) to land at the scene origin with our project's
  // local axes: +X east, +Y up, +Z south.
  const WGS84_A  = 6378137.0;
  const WGS84_E2 = 6.69437999014e-3;
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const sinLat = Math.sin(latRad), cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad), cosLon = Math.cos(lonRad);
  const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
  const Ox = (N + height) * cosLat * cosLon;
  const Oy = (N + height) * cosLat * sinLon;
  const Oz = (N * (1 - WGS84_E2) + height) * sinLat;

  // ECEF → ENU rotation. Babylon `Matrix.FromValues(m11..m44)` is
  // row-major: arg(i,j) = m_{i,j}. For row-vector v: v · M produces
  // a row vector whose components are (v · col_j(M)). So columns of M
  // must be the destination basis vectors expressed in the source
  // frame. Here columns 1/2/3 = (East, North, Up) in ECEF coords:
  //   East  = (-sinLon,         cosLon,        0     )
  //   North = (-sinLat·cosLon, -sinLat·sinLon, cosLat)
  //   Up    = ( cosLat·cosLon,  cosLat·sinLon, sinLat)
  const ecefToEnu = BABYLON.Matrix.FromValues(
    -sinLon,            -sinLat * cosLon,  cosLat * cosLon, 0,
     cosLon,            -sinLat * sinLon,  cosLat * sinLon, 0,
     0,                  cosLat,           sinLat,          0,
     0,                  0,                0,               1
  );

  // ENU (East, North, Up) → scene (X=East, Y=Up, Z=South).
  // i.e. (East, North, Up) ↦ (East, Up, -North). For row vector:
  //   m11=1 m12=0 m13=0
  //   m21=0 m22=0 m23=-1
  //   m31=0 m32=1 m33=0
  const enuToScene = BABYLON.Matrix.FromValues(
    1, 0,  0, 0,
    0, 0, -1, 0,
    0, 1,  0, 0,
    0, 0,  0, 1
  );

  // Composite rotation ECEF → scene for row-vector multiplication:
  //   v_scene_rot = v_ecef · (ecefToEnu · enuToScene)
  const R = ecefToEnu.multiply(enuToScene);
  // Translation so the project ECEF origin lands at scene origin.
  //   v_world = v_local · R + T  where T = (-O) · R
  const negO = new BABYLON.Vector3(-Ox, -Oy, -Oz);
  const T = BABYLON.Vector3.TransformCoordinates(negO, R);
  if (tiles.group) {
    tiles.group.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(R);
    tiles.group.position = T;
  }

  // ── Quality knobs (LOD tuning) ──
  // Higher errorTarget = render lower-detail tiles → faster activation
  // and visible coverage. Photoreal Google tiles look fine at ~12 px
  // SSE for the project footprint while keeping bandwidth reasonable.
  // Low-bandwidth mode triples errorTarget and slashes cache for
  // users on slow networks.
  tiles.errorTarget = lowBandwidth ? 40 : 12;
  if (tiles.lruCache) {
    tiles.lruCache.minSize = lowBandwidth ? 800 : 4000;
    tiles.lruCache.maxSize = lowBandwidth ? 1200 : 6000;
  }
  if (tiles.downloadQueue) tiles.downloadQueue.maxJobs = lowBandwidth ? 8 : 24;
  if (tiles.parseQueue)    tiles.parseQueue.maxJobs    = lowBandwidth ? 4 : 12;
  // Activate distant tiles even before they're fully traversed.
  if (tiles.displayActiveTiles !== undefined) tiles.displayActiveTiles = true;
  if (tiles.autoDisableRendererCulling !== undefined) {
    tiles.autoDisableRendererCulling = true;
  }

  // ── Wire camera + viewport ──
  // The Babylon TilesRenderer's LOD math needs camera projection +
  // viewport pixel dimensions, otherwise SSE evaluates to ~0 → only
  // the root tile activates. Push both explicitly here and refresh
  // on every render via `update()` below.
  try {
    tiles.setCamera(camera);
    const eng = scene.getEngine?.();
    const w = eng?.getRenderWidth?.() || 1920;
    const h = eng?.getRenderHeight?.() || 1080;
    if (tiles.setResolution) tiles.setResolution(camera, w, h);
  } catch (e) { /* tolerated */ }

  // ── Tone down photoreal textures so designed buildings dominate ──
  tiles.onLoadModel = (sceneNode) => {
    try {
      const meshes = sceneNode.getChildMeshes?.(false) || [];
      for (const m of meshes) {
        m.receiveShadows = true;
        if (m.material) {
          if (m.material.environmentIntensity !== undefined) {
            m.material.environmentIntensity = 0.1;
          }
          if (m.material.metallic !== undefined) {
            m.material.metallic = 0;
            m.material.roughness = 1;
          }
        }
      }
    } catch (e) { /* node may not be ready */ }
  };

  // ── Loaded callbacks ──
  const loadedCallbacks = [];
  let firstLoadFired = false;
  tiles.addEventListener("load-tile-set", () => {
    if (firstLoadFired) return;
    firstLoadFired = true;
    loadedCallbacks.forEach((cb) => { try { cb(); } catch (e) {} });
  });

  // ── Raycast for ground elevation ──
  function groundElevationAt(x, z) {
    try {
      const origin = new BABYLON.Vector3(x, 5000, z);
      const direction = new BABYLON.Vector3(0, -1, 0);
      const ray = new BABYLON.Ray(origin, direction, 10000);
      const tileRoot = tiles.group;
      if (!tileRoot) return null;
      const meshes = tileRoot.getChildMeshes(false);
      if (!meshes || meshes.length === 0) return null;
      let closestHit = null;
      for (const mesh of meshes) {
        if (!mesh.isPickable) continue;
        const pickInfo = ray.intersectsMesh(mesh, false);
        if (pickInfo.hit && pickInfo.pickedPoint) {
          if (!closestHit || pickInfo.pickedPoint.y > closestHit.y) {
            closestHit = pickInfo.pickedPoint;
          }
        }
      }
      return closestHit ? closestHit.y : null;
    } catch (e) {
      return null;
    }
  }

  // ── Public API ──
  return {
    tiles,
    group: tiles.group,

    update() {
      try {
        // Refresh viewport/resolution every frame in case the canvas
        // has been resized (e.g. mp3d tab toggle, window resize).
        const eng = scene.getEngine?.();
        const w = eng?.getRenderWidth?.() || 1920;
        const h = eng?.getRenderHeight?.() || 1080;
        if (tiles.setResolution) tiles.setResolution(camera, w, h);
        tiles.update();
      } catch (e) {}
    },

    groundElevationAt,

    onLoaded(cb) {
      if (firstLoadFired) cb();
      else loadedCallbacks.push(cb);
    },

    dispose() {
      try { tiles.dispose(); } catch (e) {}
    },
  };
}
